import * as React from 'react';
import styles from './index.less';
import { Checkbox, Space, Tabs } from 'antd';
import FComponentsLib from '@freelog/components-lib';
import FResourceTypeFilter3, { resourceTypeCodes_all } from '@/components/FResourceTypeFilter3';
import FDrawer from '@/components/FDrawer';
import * as AHooks from 'ahooks';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import dayjs from 'dayjs';
import FCoverImage from '@/components/FCoverImage';
import FResourceStatusBadge from '@/components/FResourceStatusBadge';
import FListFooter, { listStateAndListMore } from '@/components/FListFooter';
import FLoadingTip from '@/components/FLoadingTip';
import FNoDataTip from '@/components/FNoDataTip';
import FTooltip from '@/components/FTooltip';
import fPromiseModalConfirm from '@/components/fPromiseModalConfirm';
import img_choiceLabel from '@/assets/choice-label@2x.png';
import FHotspotTooltip, { fSetHotspotTooltipVisible } from '@/components/FHotspotTooltip';
import FIcons from '@/components/FIcons';
import FSimpleBar from '@/components/FSimpleBar';
import FInput_Search from '@/components/FInput_Search';
interface SelectResourceDrawerProps {
  // open: boolean;
  collectionID: string;

  onConfirm?(resourceIDs: string[]): void;

  onClosed?(): void;
}

interface SelectResourceDrawerStates {
  resourceTypeCodes: {
    value: string;
    label: string;
    values: string[];
    labels: string[];
  };
  keyword: string;
  list: {
    resourceID: string;
    resourceName: string;
    resourceTitle: string;
    cover: string;
    resourceType: string[];
    updateDate: string;
    status: 'unreleased' | 'online' | 'freeze' | 'offline';
    isAuth: boolean;
    used: boolean;
    isChoice: boolean;
  }[];
  checkedResourceIDs: string[];
  listState: 'loading' | 'noData' | 'noSearchResult' | 'loaded';
  listMore: 'loading' | 'andMore' | 'noMore';
}

const initDate: SelectResourceDrawerStates = {
  resourceTypeCodes: resourceTypeCodes_all,
  keyword: '',
  list: [],
  checkedResourceIDs: [],
  listState: 'loading',
  listMore: 'loading',
};

function SelectResourceDrawer({
  // open,
  onConfirm,
  onClosed,
  collectionID,
}: SelectResourceDrawerProps) {
  const [$open, set$open, get$open] = FUtil.Hook.useGetState<boolean>(true);
  const [$resourceTypeCodes, set$resourceTypeCodes, get$resourceTypeCodes] = FUtil.Hook.useGetState<
    SelectResourceDrawerStates['resourceTypeCodes']
  >(initDate['resourceTypeCodes']);
  const [$keyword, set$keyword, get$keyword] = FUtil.Hook.useGetState<
    SelectResourceDrawerStates['keyword']
  >(initDate['keyword']);
  const [$list, set$list, get$list] = FUtil.Hook.useGetState<SelectResourceDrawerStates['list']>(
    initDate['list'],
  );
  const [$checkedResourceIDs, set$checkedResourceIDs, get$checkedResourceIDs] =
    FUtil.Hook.useGetState<SelectResourceDrawerStates['checkedResourceIDs']>(
      initDate['checkedResourceIDs'],
    );
  const [$listState, set$listState, get$listState] = FUtil.Hook.useGetState<
    SelectResourceDrawerStates['listState']
  >(initDate['listState']);
  const [$listMore, set$listMore, get$listMore] = FUtil.Hook.useGetState<
    SelectResourceDrawerStates['listMore']
  >(initDate['listMore']);

  AHooks.useDebounceEffect(
    () => {
      fetchData(true);
    },
    [$resourceTypeCodes, $keyword],
    {
      wait: 300,
    },
  );

  AHooks.useDebounceEffect(
    () => {
      if (!($checkedResourceIDs.length === 0)) {
        fSetHotspotTooltipVisible('collectionCreator.addResourceDrawer.confirmButton', {
          value: true,
          effectiveImmediately: true,
          onlyNullish: true,
        });
      }
    },
    [$checkedResourceIDs],
    {
      wait: 10,
    },
  );

  async function fetchData(restart: boolean) {
    let inheritData: SelectResourceDrawerStates['list'] = [];
    if (!restart) {
      inheritData = get$list();
      set$listMore('loading');
    } else {
      set$listState('loading');
    }

    const resourceTypes: string[] = get$resourceTypeCodes().value.split('#');
    let resourceTypeCode: string = resourceTypes[0];
    let resourceTypeCategory: 1 | 2 = 1;
    let resourceType: string = '';

    if (
      resourceTypes.length > 1 &&
      resourceTypes[resourceTypes.length - 1] === 'other'
      // && resourceTypes.at(-1) === 'other'
    ) {
      resourceTypeCode = resourceTypes[0];
      resourceTypeCategory = 2;
    }

    if (
      resourceTypes.length > 1 &&
      resourceTypes[resourceTypes.length - 1] === 'all'
      // && resourceTypes.at(-1) === 'all'
    ) {
      resourceType = get$resourceTypeCodes().labels[get$resourceTypeCodes().labels.length - 2];
    }

    const params: Parameters<typeof FServiceAPI.Resource.list>[0] = {
      skip: inheritData.length,
      limit: FUtil.Predefined.pageSize,
      keywords: get$keyword() || undefined,
      resourceType: resourceType || undefined,
      resourceTypeCode: resourceType === '' ? resourceTypeCode || undefined : undefined,
      resourceTypeCategory: resourceType === '' ? resourceTypeCategory : undefined,
      // status: resourceListPage.resourceStatus === '#' ? undefined : (resourceListPage.resourceStatus as 0),
      isSelf: 1,
      subjectType: 1,
    };

    const {
      ret,
      errCode,
      data: data_resourceList,
    }: {
      ret: number;
      errCode: number;
      data: {
        limit: number;
        skip: number;
        totalItem: number;
        dataList: {
          resourceId: string;
          resourceName: string;
          resourceTitle: string;
          resourceType: string[];
          latestVersion: string;
          coverImages: string[];
          status: 0 | 1 | 2 | 4;
          policies: {
            policyId: string;
            policyName: string;
            status: 0 | 1;
          }[];
          userId: number;
          updateDate: string;
          username: string;
          operationType: 0 | 1 | 2 | 3 | 4 | 5;
        }[];
      };
    } = await FServiceAPI.Resource.list(params);

    let auths: {
      isAuth: boolean;
      resourceId: string;
    }[] = [];
    let usedResources: {
      isExists: boolean;
      resourceId: string;
    }[] = [];
    const resourceIds: string = data_resourceList.dataList
      .filter((r) => {
        return r.latestVersion !== '';
      })
      .map((r) => {
        return r.resourceId;
      })
      .join(',');
    if (resourceIds !== '') {
      const {
        data: data1,
      }: {
        data: {
          isAuth: boolean;
          resourceId: string;
        }[];
      } = await FServiceAPI.Resource.batchAuth({
        resourceIds: resourceIds,
      });
      auths = data1;

      const {
        data: data2,
      }: {
        data: {
          isExists: boolean;
          resourceId: string;
        }[];
      } = await FServiceAPI.Resource.resourceIsExistInItems_Draft({
        resourceId: collectionID,
        resourceIds: resourceIds,
      });
      usedResources = data2;
    }

    const list: SelectResourceDrawerStates['list'] = [
      ...inheritData,
      ...data_resourceList.dataList.map<SelectResourceDrawerStates['list'][number]>((d) => {
        const res = auths.find((dd) => {
          return dd.resourceId === d.resourceId;
        });

        const resUsed = (usedResources || []).find((dd) => {
          return dd.resourceId === d.resourceId;
        });

        return {
          resourceID: d.resourceId,
          resourceName: d.resourceName,
          resourceTitle: d.resourceTitle,
          cover: d.coverImages[0] || '',
          resourceType: d.resourceType,
          updateDate: dayjs(d.updateDate).format(FUtil.Predefined.momentDateFormat),
          status:
            d.status === 2
              ? 'freeze'
              : d.status === 1
              ? 'online'
              : d.status === 0
              ? 'unreleased'
              : 'offline',
          isAuth: !res || res.isAuth,
          used: !!resUsed?.isExists,
          isChoice: d.operationType === 5,
        };
      }),
    ];

    const { state, more } = listStateAndListMore({
      list_Length: list.length,
      total_Length: data_resourceList.totalItem,
      has_FilterCriteria: get$keyword() !== '' || get$resourceTypeCodes().value !== '#all',
    });
    set$listState(state);
    // set$listState('noData');
    set$listMore(more);
    set$list(list);
  }

  function changeChecked(resourceID: string) {
    if (get$checkedResourceIDs().includes(resourceID)) {
      set$checkedResourceIDs(
        get$checkedResourceIDs().filter((v) => {
          return v !== resourceID;
        }),
      );
    } else {
      set$checkedResourceIDs([...get$checkedResourceIDs(), resourceID]);
    }
  }

  return (
    <FDrawer
      width={720}
      open={$open}
      // title={'选择单品资源'}
      title={FI18n.i18nNext.tAuto('cqr_itemmgmt_additem_title')}
      afterOpenChange={(o) => {
        if (!o) {
          onClosed && onClosed();
        } else {
          // fetchData(true);
        }
      }}
      topRight={
        <Space size={30}>
          <FComponentsLib.FTextBtn
            onClick={() => {
              // set_visible(false);
              // onCancel && onCancel();
              set$open(false);
            }}
            type='default'
          >
            {FI18n.i18nNext.tAuto('cqr_itemmgmt_additem_btn_cancel')}
          </FComponentsLib.FTextBtn>
          <FHotspotTooltip
            id={'collectionCreator.addResourceDrawer.confirmButton'}
            style={{ left: 20, bottom: -40 }}
            text={FI18n.i18nNext.tAuto('hotpots_cqr_additem_add')}
            onOpenChange={(o) => {
              if (o) {
                fSetHotspotTooltipVisible('collectionCreator.addResourceDrawer.confirmButton', {
                  value: false,
                  effectiveImmediately: false,
                  onlyNullish: false,
                });
              }
            }}
          >
            <FComponentsLib.FRectBtn
              disabled={$checkedResourceIDs.length === 0}
              onClick={async () => {
                fSetHotspotTooltipVisible('collectionCreator.addResourceDrawer.confirmButton', {
                  value: false,
                  effectiveImmediately: true,
                  onlyNullish: false,
                });
                onConfirm && onConfirm(get$checkedResourceIDs());
                set$open(false);
              }}
            >
              {FI18n.i18nNext.tAuto('cqr_itemmgmt_additem_btn_add')}
            </FComponentsLib.FRectBtn>
          </FHotspotTooltip>
        </Space>
      }
      style={{ overflowY: 'auto', overflowX: 'hidden' }}
      contentStyle={{ padding: 0 }}
      drawerBodyStyle={{ width: '100%' }}
    >
      <div className={styles.container}>
        <div style={{ height: 8 }} />
        <div style={{ padding: '0 30px' }}>
          <Tabs
            items={[
              {
                key: '1',
                label: FI18n.i18nNext.tAuto('cqr_itemmgmt_additem_tab_myresources'),
              },
            ]}
          />
        </div>

        {$listState === 'noData' && (
          <FNoDataTip
            height={600}
            // tipText={'未创建资源'}
            tipText={FI18n.i18nNext.tAuto('cqr_itemmgmt_additem_empty_msg')}
            // btnText={'创建资源'}
            // btnText={FI18n.i18nNext.tAuto('cqr_itemmgmt_additem_empty_btn')}
            btn={
              <FHotspotTooltip
                id={'collectionCreator.addResourceDrawer.createResourceButton'}
                style={{ left: 32, bottom: -38 }}
                text={FI18n.i18nNext.tAuto('hotpots_cqr_additem_createresource')}
                onOpenChange={(o) => {
                  if (o) {
                    fSetHotspotTooltipVisible(
                      'collectionCreator.addResourceDrawer.createResourceButton',
                      {
                        value: false,
                        effectiveImmediately: false,
                        onlyNullish: false,
                      },
                    );
                  }
                }}
              >
                <FComponentsLib.FRectBtn
                  // disabled={$checkedResourceIDs.length === 0}
                  onClick={async () => {
                    fSetHotspotTooltipVisible(
                      'collectionCreator.addResourceDrawer.createResourceButton',
                      {
                        value: false,
                        effectiveImmediately: true,
                        onlyNullish: false,
                      },
                    );
                    self.open(FUtil.LinkTo.resourceCreatorEntry());
                    const result: boolean = await fPromiseModalConfirm({
                      title: '提示',
                      // description: '已完成资源的创建',
                      description: FI18n.i18nNext.t(
                        'cqr_itemmgmt_additem_newresource_confirmation',
                      ),
                      // cancelText: '否',
                      cancelText: FI18n.i18nNext.t(
                        'cqr_itemmgmt_additem_newresource_confirmation_btn_no',
                      ),
                      // okText: '是',
                      okText: FI18n.i18nNext.t(
                        'cqr_itemmgmt_additem_newresource_confirmation_btn_yes',
                      ),
                      // getContainer: () => {
                      //   console.log(refContainer.current, 'refContainer.current 8iwfsj;lksdjlfkjsdkjj');
                      //   return refContainer.current;
                      // },
                    });

                    if (result) {
                      fetchData(true);
                    }
                  }}
                >
                  {FI18n.i18nNext.tAuto('cqr_itemmgmt_additem_empty_btn')}
                </FComponentsLib.FRectBtn>
              </FHotspotTooltip>
            }
            // onClick={() => {
            //
            // }}
          />
        )}

        {$listState !== 'noData' && (
          <>
            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 30px',
              }}
            >
              <Space size={30}>
                <FResourceTypeFilter3
                  subjectTypeArr={['resource']}
                  value={$resourceTypeCodes}
                  onChange={(value) => {
                    set$resourceTypeCodes(value);
                  }}
                />
                <FComponentsLib.FTextBtn
                  type={'primary'}
                  onClick={async () => {
                    // fSetHotspotTooltipVisible('collectionCreator.addResourceDrawer.createResourceButton', {
                    //   value: false,
                    //   effectiveImmediately: true,
                    //   onlyNullish: false,
                    // });
                    self.open(FUtil.LinkTo.resourceCreatorEntry());
                    const result: boolean = await fPromiseModalConfirm({
                      title: '提示',
                      // description: '已完成资源的创建',
                      description: FI18n.i18nNext.t(
                        'cqr_itemmgmt_additem_newresource_confirmation',
                      ),
                      // cancelText: '否',
                      cancelText: FI18n.i18nNext.t(
                        'cqr_itemmgmt_additem_newresource_confirmation_btn_no',
                      ),
                      // okText: '是',
                      okText: FI18n.i18nNext.t(
                        'cqr_itemmgmt_additem_newresource_confirmation_btn_yes',
                      ),
                      // getContainer: () => {
                      //   console.log(refContainer.current, 'refContainer.current 8iwfsj;lksdjlfkjsdkjj');
                      //   return refContainer.current;
                      // },
                    });

                    if (result) {
                      fetchData(true);
                    }
                  }}
                >
                  {FI18n.i18nNext.tAuto('cqr_itemmgmt_additem_btn_createresource')}
                </FComponentsLib.FTextBtn>
                {/*</FHotspotTooltip>*/}
                <FComponentsLib.FContentText
                  type={'normal'}
                  // text={`已选择${$checkedResourceIDs.length}/100`}
                  text={FI18n.i18nNext.t('cqr_itemmgmt_additem_selectedqty', {
                    selectedqty: $checkedResourceIDs.length,
                  })}
                  style={{ color: '#999' }}
                />
              </Space>

              {/* <FComponentsLib.FInput.FSearch */}
              <FInput_Search
                value={$keyword}
                onChange={(e) => {
                  set$keyword(e.target.value);
                }}
                placeholder={FI18n.i18nNext.t('cqr_itemmgmt_additem_search_hint')}
              />
            </div>
            <div style={{ height: 20 }} />

            {$listState === 'loading' && <FLoadingTip height={600} />}

            {$listState === 'noSearchResult' && <FNoDataTip height={600} tipText={'无搜索数据'} />}

            {$listState === 'loaded' && (
              <FSimpleBar height={'calc(100vh - 200px)'}>
                <div
                  style={{
                    // height: 'calc(100vh - 200px)',
                    // overflowY: 'auto',
                    padding: '0 30px',
                  }}
                >
                  <div className={styles.list}>
                    {$list.map((d) => {
                      const isItemDisabled =
                        d.status !== 'online' ||
                        d.used ||
                        ($checkedResourceIDs.length >= 100 &&
                          !$checkedResourceIDs.includes(d.resourceID));
                      return (
                        <div
                          className={[
                            styles.item,
                            isItemDisabled ? styles.disabled : '',
                            $checkedResourceIDs.includes(d.resourceID) ? styles.selected : '',
                          ].join(' ')}
                          key={d.resourceID}
                        >
                          <div className={styles.resource}>
                            <div
                              className={isItemDisabled ? styles.partFaded : undefined}
                              style={{
                                width: 16,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Checkbox
                                checked={$checkedResourceIDs.includes(d.resourceID) || d.used}
                                onChange={() => {
                                  if (isItemDisabled) {
                                    return;
                                  }
                                  changeChecked(d.resourceID);
                                }}
                              />
                            </div>
                            <div style={{ width: 20 }} />
                            <div
                              className={styles.resourceInfo}
                              onClick={() => {
                                if (isItemDisabled) {
                                  return;
                                }
                                changeChecked(d.resourceID);
                              }}
                            >
                              <div className={styles.cover}>
                                <div
                                  className={[
                                    styles.coverImage,
                                    isItemDisabled ? styles.partFaded : '',
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                >
                                  <FCoverImage
                                    src={d.cover}
                                    width={104}
                                    style={{ borderRadius: 4 }}
                                  />
                                </div>
                                <div className={styles.coverOverlay}>
                                  <FResourceStatusBadge
                                    status={d.status}
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 600,
                                      lineHeight: 1.6,
                                      padding: '0 3px',
                                      borderRadius: 3,
                                      maxWidth: '100%',
                                      minWidth: 0,
                                    }}
                                  />
                                  {d.isChoice && (
                                    <div
                                      className={isItemDisabled ? styles.partFaded : undefined}
                                    >
                                      <FTooltip title={'编辑精选'} placement={'bottomLeft'}>
                                        <img
                                          src={img_choiceLabel}
                                          alt={''}
                                          style={{ width: 20, height: 20 }}
                                        />
                                      </FTooltip>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div
                                className={[
                                  styles.content,
                                  isItemDisabled ? styles.partFaded : '',
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                              >
                                <FComponentsLib.FContentText
                                  text={d.resourceTitle || d.resourceName.split('/')[1]}
                                  type={'highlight'}
                                  singleRow
                                />
                                <Space size={5}>
                                  <FComponentsLib.FContentText
                                    text={d.resourceType.join('/')}
                                    type={'additional2'}
                                  />
                                  <FComponentsLib.FContentText text={'|'} type={'additional2'} />
                                  <FComponentsLib.FContentText
                                    // text={`最近更新${d.updateDate}`}
                                    text={FI18n.i18nNext.t(
                                      'cqr_itemmgmt_additem_entry_lastupdated',
                                      {
                                        Timestamp: d.updateDate,
                                      },
                                    )}
                                    type={'additional2'}
                                  />
                                </Space>
                              </div>
                              {!d.isAuth && (
                                <div
                                  className={isItemDisabled ? styles.partFaded : undefined}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    alignSelf: 'center',
                                  }}
                                >
                                  <FTooltip title={'该资源授权链异常，请谨慎签约。'}>
                                    <div style={{ alignSelf: 'center' }}>
                                      <FIcons.FWarning style={{ fontSize: 14 }} />
                                    </div>
                                  </FTooltip>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <FListFooter
                    state={$listMore}
                    onClickLoadMore={() => {
                      fetchData(false);
                    }}
                  />
                </div>
              </FSimpleBar>
            )}
          </>
        )}
      </div>
    </FDrawer>
  );
}

export default SelectResourceDrawer;
